import React from 'react';
import { useDataContext } from '../../context/data.js';
import translations from '../../constants/translations.json';

const OrderTypeSelection = ({ orderTypeOptions, onContinue }) => {
  const { language } = useDataContext();

  const getOptionIcon = (optionId, isDisabled) => {
    const sizeClasses =
      orderTypeOptions?.length > 2
        ? 'w-[210px] sm:w-[250px] md:w-[300px] h-[210px] sm:h-[250px] md:h-[300px]'
        : 'w-[160px] sm:w-[200px] md:w-[250px] h-[160px] sm:h-[200px] md:h-[250px]';
    // A greyed-out tile has to READ as unavailable at a glance, and the label
    // alone does not carry that far — the illustration is the biggest thing on
    // it, so it desaturates with the rest.
    const stateClasses = isDisabled ? 'grayscale opacity-40' : '';

    switch (optionId) {
      case 'dine-in':
        return (
          <img
            src="/dine-in-graphic.png"
            alt="Dine-in illustration"
            className={`${sizeClasses} ${stateClasses} object-contain p-4`}
          />
        );
      case 'take-away':
        return (
          <img
            src="/take-away-graphic.png"
            alt="Take-away illustration"
            className={`${sizeClasses} ${stateClasses} object-contain p-4`}
          />
        );
      case 'delivery':
        return (
          <img
            src="/delivery-graphic.jpg"
            alt="Delivery illustration"
            className={`${sizeClasses} ${stateClasses} object-contain p-4`}
          />
        );
      // Drawn to match the other three: same flat style, same warm palette, no
      // outlines. An inline icon in the primary green sat visibly apart from
      // them.
      case 'theater':
        return (
          <img
            src="/pre-order-graphic.svg"
            alt="Pre-order illustration"
            className={`${sizeClasses} ${stateClasses} object-contain p-4`}
          />
        );
      // Same family again: flat shapes, warm tans, one red accent. Two tickets
      // with the notch a torn stub leaves, and the QR on the front one —
      // which is the thing the guest actually holds up at the door.
      case 'tickets':
        return (
          <img
            src="/tickets-graphic.svg"
            alt="Tickets illustration"
            className={`${sizeClasses} ${stateClasses} object-contain p-4`}
          />
        );
      default:
        return null;
    }
  };

  // Nothing to offer at all. Mapping an empty list rendered a heading over
  // white space — the guest was left with a page that looked broken rather
  // than a venue that had nothing on sale, and no reason to come back. Reached
  // when a theatre's programme has run out entirely; every other reason for an
  // empty list is caught upstream by venueSellsOnline.
  if (!orderTypeOptions?.length) {
    return (
      <div className="max-w-[40rem] mx-auto px-3 md:px-4 py-10 text-center">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
          {translations[language]?.order_type_none_title || 'Der kan ikke bestilles lige nu'}
        </h1>
        <p className="text-sm md:text-base text-gray-500">
          {translations[language]?.order_type_none_message
            || 'Ingen forestillinger er åbne for forudbestilling lige nu. Bestillingen åbner tættere på forestillingsdagen.'}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`${orderTypeOptions?.length > 2 ? '' : 'max-w-[60rem]'} mx-auto px-3 md:px-4 space-y-4 md:space-y-6`}
    >
      <div className="text-center">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
          {translations[language]?.order_type_selection_title}
        </h1>
      </div>

      <div
        className={`grid grid-cols-1 ${orderTypeOptions?.length > 2 ? 'lg:grid-cols-3' : 'md:grid-cols-2'} gap-6`}
      >
        {orderTypeOptions.map((option) => {
          // A disabled tile stays ON SCREEN rather than disappearing: the guest
          // needs to see that ordering now is a thing this venue does, just not
          // at this hour — otherwise a theater outside opening hours looks like
          // a venue that only ever takes pre-orders.
          const isDisabled = Boolean(option.disabled);

          return (
            <div
              key={option.id}
              className={`order-type-selection-item p-6 md:p-8 text-center bg-white rounded-[10px] sm:rounded-[20px] overflow-hidden ${
                isDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:border-primary cursor-pointer'
              }`}
              aria-disabled={isDisabled}
              onClick={() => {
                if (isDisabled) return;
                onContinue(option.id);
              }}
            >
              {/* Icon/Illustration */}
              {getOptionIcon(option.id, isDisabled)}

              {/* Title */}
              <h3
                className={`text-base sm:text-lg md:text-xl font-semibold ${
                  isDisabled ? 'text-gray-400' : 'text-gray-800'
                }`}
              >
                {option.label}
              </h3>

              {/* Why it can't be picked right now, and when it can be */}
              {option.note && (
                <p className="mt-1 text-sm text-gray-500 font-medium">{option.note}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderTypeSelection;
